'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  getRedirectResult,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithRedirect,
} from 'firebase/auth';
import { ChevronLeft } from 'lucide-react';

import { PATHS } from '@/constants/paths';
import { useAuth } from '@/hooks/useAuth';
import { signInWithGoogle } from '@/lib/auth';
import { getAuthInstance, googleProvider, isEmulatorEnabled } from '@/lib/firebase';
import { getFunctionsBaseUrl } from '@/lib/functions-url';

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false); // Hydration guard
  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const router = useRouter();
  const { isRegistered, needsProfileCompletion, isDataLoading } = useAuth();

  // Hydration guard: クライアント側でのみ判定
  useEffect(() => {
    setMounted(true);
    // クライアント側でURLパラメータを取得（静的エクスポート対応）
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      setSearchParams(params);

      // URLパラメータからメッセージを取得
      const msg = params.get('message');
      if (msg) {
        setMessage(decodeURIComponent(msg));
      }

      // SessionStorageからメールアドレスを取得
      const savedEmail = sessionStorage.getItem('loginEmail');
      if (savedEmail) {
        setEmail(savedEmail);
        sessionStorage.removeItem('loginEmail');
      }
    }
  }, []);

  const isWidgetMode = mounted && searchParams ? searchParams.get('mode') === 'widget' : false;
  const isDebug = mounted && searchParams ? searchParams.get('debug') === '1' : false;
  const redirectPath = mounted && searchParams ? searchParams.get('redirect') : null;

  // ログイン済みの場合はリダイレクト（useEffect内で実行）。ゲスト(匿名ログイン)は対象外
  useEffect(() => {
    if (isWidgetMode) return; // ブローカーモード中はホームへリダイレクトしない
    if (!isDataLoading && isRegistered) {
      const safeRedirectPath =
        redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('//')
          ? redirectPath
          : null;
      if (needsProfileCompletion) {
        const profilePath = safeRedirectPath
          ? `${PATHS.AUTH.SIGNUP.STEP4}?redirect=${encodeURIComponent(safeRedirectPath)}`
          : PATHS.AUTH.SIGNUP.STEP4;
        router.push(profilePath);
        return;
      }

      if (safeRedirectPath) {
        router.push(safeRedirectPath);
      } else {
        router.push(PATHS.HOME);
      }
    }
  }, [isRegistered, needsProfileCompletion, isDataLoading, router, isWidgetMode, redirectPath]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      if (isWidgetMode) {
        // Widgetブローカーモードではpopupを使わずredirectで開始（COOP/ポップアップ問題回避）
        await signInWithRedirect(getAuthInstance(), googleProvider);
      } else {
        await signInWithGoogle();
      }
      // useAuthフックが自動的にリダイレクトを処理
    } catch (error) {
      console.error('Login error:', error);
      setError('ログインに失敗しました。もう一度お試しください');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(getAuthInstance(), email, password);
      // useAuthフックが自動的にリダイレクトを処理
    } catch (error) {
      console.error('Email login error:', error);
      const firebaseError = error as { code?: string };
      if (firebaseError.code === 'auth/user-not-found') {
        setError('このメールアドレスは登録されていません');
      } else if (firebaseError.code === 'auth/wrong-password') {
        setError('パスワードが正しくありません');
      } else if (firebaseError.code === 'auth/invalid-email') {
        setError('メールアドレスの形式が正しくありません');
      } else if (firebaseError.code === 'auth/invalid-credential') {
        setError('メールアドレスまたはパスワードが正しくありません');
      } else {
        setError('ログインに失敗しました。もう一度お試しください');
      }
    } finally {
      setLoading(false);
    }
  };

  // Widgetブローカーモード: mode=widget のときはサインイン後にカスタムトークンを返す
  useEffect(() => {
    if (!mounted || !searchParams) return;

    const mode = searchParams.get('mode');
    const targetOrigin = searchParams.get('origin') || window.origin;
    if (mode !== 'widget') return;

    // debugパラメータはGoogleのリダイレクトで消える可能性があるためセッションに保持
    try {
      const debugParam = searchParams.get('debug');
      if (debugParam === '1') {
        sessionStorage.setItem('nomiss_widget_debug', '1');
      }
    } catch {
      // no-op
    }

    let hasSentPayload = false; // 一度だけ送信するためのフラグ
    let isProcessing = false; // 処理中フラグ（競合防止）
    let unsubscribeFn: (() => void) | null = null; // unsubscribe関数を保持

    // 埋め込み先の企業ページが受け取る。プロバイダ由来の displayName は本名であることが多く、
  // 他社に渡してよい情報ではないので載せない(2026-09-29)。ウィジェットはニックネームを使う。
  const sendAndClose = async (payload: object) => {
      if (hasSentPayload) {
        console.log('⚠️ 既にpayloadを送信済みのためスキップ');
        return;
      }
      hasSentPayload = true;
      isProcessing = false; // 処理完了

      try {
        window.opener?.postMessage(payload, targetOrigin);
      } catch {
        // no-op
      }

      console.log('nomiss-auth payload:', payload);

      // payload送信後、リスナーを解除
      if (unsubscribeFn) {
        unsubscribeFn();
        unsubscribeFn = null;
      }

      // デバッグモードの場合は詳細情報を表示
      try {
        if (isDebug || sessionStorage.getItem('nomiss_widget_debug') === '1') {
          alert(
            `nomiss-auth payload:\n${JSON.stringify(payload, null, 2)}\n\n自動クローズは一時停止中です。手動でウィンドウを閉じてください。`
          );
          return; // デバッグモードでは閉じない
        }
      } catch {
        // no-op
      }

      // ⚠️ デバッグ用: Popupを閉じないようにする（一時的）
      // ログを確認するために、Popupを開いたままにします
      console.log('✅ [sendAndClose] Payload送信完了。Popupは閉じません（デバッグモード）');

      // ログイン成功メッセージを表示（閉じない）
      try {
        // ページ全体をローディング状態にする
        document.body.style.opacity = '0.5';
        document.body.style.pointerEvents = 'none';

        // 成功メッセージを表示
        const messageDiv = document.createElement('div');
        messageDiv.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          padding: 24px 32px;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          z-index: 10000;
          text-align: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        `;
        messageDiv.innerHTML = `
          <div style="font-size: 18px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px;">
            ✅ ログインに成功しました
          </div>
          <div style="font-size: 14px; color: #666; margin-top: 8px;">
            デバッグモード: Popupは閉じません<br/>
            コンソールでログを確認してください
          </div>
          <button onclick="window.close()" style="margin-top: 16px; padding: 8px 16px; background: #52a2e8; color: white; border: none; border-radius: 6px; cursor: pointer;">
            手動で閉じる
          </button>
        `;
        document.body.appendChild(messageDiv);

        // ⚠️ Popupを閉じる処理をコメントアウト（デバッグ用）
        // setTimeout(() => {
        //   if (window.opener && !window.closed) {
        //     window.close();
        //   }
        // }, 1500);
      } catch (error) {
        // エラーが発生した場合も閉じない（デバッグ用）
        console.error('Error showing success message:', error);
        // ⚠️ Popupを閉じる処理をコメントアウト（デバッグ用）
        // if (window.opener && !window.closed) {
        //   window.close();
        // }
      }
    };

    // リダイレクト結果を確認し、custom tokenを取得して送信
    void (async () => {
      if (isProcessing || hasSentPayload) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ [getRedirectResult] 既に処理中または送信済みのためスキップ');
        }
        return; // 既に処理中または送信済み
      }
      isProcessing = true;

      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 [getRedirectResult] 処理開始');
      }

      try {
        const authInstance = getAuthInstance();
        const result = await getRedirectResult(authInstance);
        if (process.env.NODE_ENV === 'development') {
          console.log('📋 [getRedirectResult] 結果:', {
            hasResult: !!result,
            hasUser: !!result?.user,
            currentUser: !!authInstance.currentUser,
          });
        }

        // リダイレクト結果がある場合、または既にサインイン済みの場合
        const currentUser = result?.user || authInstance.currentUser;
        if (!currentUser) {
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ [getRedirectResult] ユーザーがいないため、onAuthStateChangedで処理');
          }
          isProcessing = false; // ユーザーがいない場合は処理終了
          return; // ユーザーがいない場合はonAuthStateChangedで処理
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [getRedirectResult] ユーザーを取得:', {
            uid: currentUser.uid,
            email: currentUser.email,
          });
        }

        // ✅ Emulator環境でもCustom Tokenを取得して送信
        // これにより、Widget側でFirebase Auth SDKでサインインでき、Firestore Security Rulesを通過できる
        const isEmulator = isEmulatorEnabled();

        if (process.env.NODE_ENV === 'development') {
          console.log('🔧 Environment check:', { isEmulator, hostname: window.location.hostname });
        }

        try {
          const idToken = await currentUser.getIdToken(/* forceRefresh */ true);

          // Functions URLを設定
          // Emulator環境: ローカルのEmulator URL
          // 本番環境: デプロイされたFunctions URL（環境変数または自動生成）
          const base = getFunctionsBaseUrl();

          if (process.env.NODE_ENV === 'development') {
            console.log('📡 Calling createCustomToken:', base);
          }

          const resp = await fetch(`${base}/createCustomToken`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          });

          const json = await resp.json();

          if (process.env.NODE_ENV === 'development') {
            console.log('📦 [getRedirectResult] createCustomToken response:', {
              ok: resp.ok,
              status: resp.status,
              hasToken: !!json?.token,
              hasIdToken: !!json?.idToken,
              error: json?.error,
              responseKeys: Object.keys(json),
              fullResponse: json, // デバッグ用: 実際のレスポンス内容を表示
            });
          }

          // ✅ tokenまたはidTokenのいずれかがあれば処理を続行
          // Emulator環境ではidTokenが返される場合があるため、両方をチェック
          const token = json?.token || json?.idToken;

          if (resp.ok && token) {
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Custom token obtained, sending to widget:', {
                hasToken: !!json?.token,
                hasIdToken: !!json?.idToken,
                usingToken: !!json?.token,
                usingIdToken: !!json?.idToken && !json?.token,
              });
            }
            // ✅ Custom Tokenとユーザー情報の両方を送信
            sendAndClose({
              type: 'nomiss-auth',
              token: token, // tokenまたはidTokenのいずれか
              uid: currentUser.uid,
              email: currentUser.email,
              photoURL: currentUser.photoURL,
              emailVerified: currentUser.emailVerified,
            });
            return;
          } else {
            console.error('❌ [getRedirectResult] Custom token取得失敗:', {
              status: resp.status,
              error: json?.error || 'Unknown error',
              response: json,
            });
            isProcessing = false;
            // エラー時のフォールバック: ユーザー情報のみ送信（Firestore アクセスは制限される）
            console.warn(
              '⚠️ [getRedirectResult] Falling back to user info only (Firestore access will be limited)'
            );
            sendAndClose({
              type: 'nomiss-auth',
              error: 'Custom token generation failed',
              uid: currentUser.uid,
              email: currentUser.email,
              photoURL: currentUser.photoURL,
              emailVerified: currentUser.emailVerified,
            });
          }
        } catch (customTokenError) {
          console.error('❌ [getRedirectResult] Custom token取得エラー:', customTokenError);
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ [getRedirectResult] エラー詳細:', {
              message:
                customTokenError instanceof Error
                  ? customTokenError.message
                  : String(customTokenError),
              stack: customTokenError instanceof Error ? customTokenError.stack : undefined,
            });
          }
          isProcessing = false;
          // エラー時のフォールバック
          sendAndClose({
            type: 'nomiss-auth',
            error: customTokenError instanceof Error ? customTokenError.message : 'Unknown error',
            uid: currentUser.uid,
            email: currentUser.email,
            photoURL: currentUser.photoURL,
            emailVerified: currentUser.emailVerified,
          });
        }

        // custom tokenが取得できない場合のみ、OAuthフォールバック（今は無効化されているためコメントアウト）
        // const cred = result ? (await import('firebase/auth')).GoogleAuthProvider.credentialFromResult(result) : null;
        // const accessToken = cred?.accessToken || null;
        // if (accessToken) {
        //   sendAndClose({
        //     type: 'nomiss-auth',
        //     provider: 'google-oauth',
        //     accessToken: accessToken,
        //   });
        //   return;
        // }
      } catch (e) {
        console.error('getRedirectResult処理エラー:', e);
        isProcessing = false; // エラーの場合は処理終了
        // 続行（onAuthStateChangedで通常フロー）
      }
    })();

    unsubscribeFn = onAuthStateChanged(getAuthInstance(), async (currentUser) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 [onAuthStateChanged] 発火:', {
          hasSentPayload,
          isProcessing,
          hasUser: !!currentUser,
          uid: currentUser?.uid,
        });
      }

      if (hasSentPayload) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ [onAuthStateChanged] 既にpayloadを送信済みのためスキップ');
        }
        return;
      }
      if (isProcessing) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ [onAuthStateChanged] getRedirectResult処理中のためスキップ');
        }
        return;
      }
      isProcessing = true;

      try {
        // A guest session from the live page is not a login the widget can use.
        if (!currentUser || currentUser.isAnonymous) {
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ [onAuthStateChanged] ユーザーがいないため、リダイレクト開始');
          }
          // 連打・リロードでの多重リダイレクトを防ぐためフラグで制御
          const key = 'nomiss_widget_auth_redirect_inflight';
          const inflight = sessionStorage.getItem(key);
          if (!inflight) {
            sessionStorage.setItem(key, '1');
            await signInWithRedirect(getAuthInstance(), googleProvider);
          }
          isProcessing = false; // リダイレクト開始時は処理終了
          return;
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [onAuthStateChanged] ユーザーを取得:', {
            uid: currentUser.uid,
            email: currentUser.email,
          });
        }

        const idToken = await currentUser.getIdToken(/* forceRefresh */ true);

        // Emulator環境の判定
        const isEmulator = isEmulatorEnabled();

        // Emulator用のFunctions URLを設定
        const base = getFunctionsBaseUrl();

        if (process.env.NODE_ENV === 'development') {
          console.log('📡 [onAuthStateChanged] Calling createCustomToken:', { base, isEmulator });
        }

        const resp = await fetch(`${base}/createCustomToken`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (process.env.NODE_ENV === 'development') {
          console.log('📡 [onAuthStateChanged] Fetch response:', {
            ok: resp.ok,
            status: resp.status,
            statusText: resp.statusText,
          });
        }
        const json = await resp.json();

        if (process.env.NODE_ENV === 'development') {
          console.log('📦 [onAuthStateChanged] createCustomToken response:', {
            ok: resp.ok,
            status: resp.status,
            hasToken: !!json?.token,
            hasIdToken: !!json?.idToken,
            error: json?.error,
            responseKeys: Object.keys(json),
            fullResponse: json,
          });
        }

        // ✅ tokenまたはidTokenのいずれかがあれば処理を続行
        // Emulator環境ではidTokenが返される場合があるため、両方をチェック
        const token = json?.token || json?.idToken;

        if (resp.ok && token) {
          try {
            sessionStorage.removeItem('nomiss_widget_auth_redirect_inflight');
          } catch {}
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ [onAuthStateChanged] Custom token obtained, sending to widget:', {
              hasToken: !!json?.token,
              hasIdToken: !!json?.idToken,
              usingToken: !!json?.token,
              usingIdToken: !!json?.idToken && !json?.token,
            });
          }
          // ✅ Custom Tokenとユーザー情報の両方を送信
          sendAndClose({
            type: 'nomiss-auth',
            token: token, // tokenまたはidTokenのいずれか
            uid: currentUser.uid,
            email: currentUser.email,
            photoURL: currentUser.photoURL,
            emailVerified: currentUser.emailVerified,
          });
        } else {
          try {
            sessionStorage.removeItem('nomiss_widget_auth_redirect_inflight');
          } catch {}
          isProcessing = false; // エラーの場合は処理終了
          console.error(
            '❌ [onAuthStateChanged] Custom token取得失敗:',
            json?.error || 'Failed to mint token'
          );
          sendAndClose({ type: 'nomiss-auth', error: json?.error || 'Failed to mint token' });
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        try {
          sessionStorage.removeItem('nomiss_widget_auth_redirect_inflight');
        } catch {}
        isProcessing = false; // エラーの場合は処理終了
        sendAndClose({ type: 'nomiss-auth', error: msg });
      }
    });

    return () => {
      if (unsubscribeFn) {
        unsubscribeFn();
      }
    };
  }, [mounted, searchParams, isDebug]);

  // Hydration guard: マウント前は空のdivを返す
  if (!mounted) {
    return <div suppressHydrationWarning className="min-h-screen bg-white" />;
  }

  // ローディング中またはログイン済みの場合の表示
  // widgetモードでは白画面を避けて簡易ステータスを表示
  if (isDataLoading || isRegistered) {
    if (isWidgetMode) {
      return (
        <div className="flex min-h-screen items-center justify-center" suppressHydrationWarning>
          <div className="text-center text-gray-600">
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
            <p>サインイン状態を確認中...</p>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden rounded-t-[20px] bg-white">
      {/* グラデーション背景 */}
      <div className="absolute left-0 top-0 h-[185px] w-full bg-gradient-to-b from-[#52a2e8] to-[#c4dff0]" />

      {/* 戻るボタン */}
      <button
        onClick={() => router.back()}
        className="absolute left-[25px] top-[30px] z-10 flex h-8 w-[14px] cursor-pointer items-center justify-center"
      >
        <ChevronLeft className="h-8 w-8 text-black" strokeWidth={2} />
      </button>

      {/* メインコンテンツ */}
      <div className="relative z-10 flex flex-1 flex-col items-center px-6">
        {/* ショッピングイラスト */}
        <div className="relative mt-[30px] h-[184px] w-[184px]">
          <Image
            src="/image/shopping.png"
            alt="ショッピング"
            fill
            className="object-contain"
            priority
          />
        </div>

        {/* タイトル */}
        <h1 className="mt-[34px] text-[22px] font-semibold leading-[20px] tracking-[-0.32px] text-black">
          ライブショッピングへようこそ！
        </h1>

        {/* 説明文 */}
        <p className="mt-[27px] w-[332px] text-center text-[16px] font-medium leading-[23px] tracking-[-0.32px] text-black">
          購入やコメント・クーポンなどの利用はログインが必要です
        </p>

        {/* メッセージ表示 */}
        {message && (
          <div className="mt-4 w-[348px] rounded-md bg-blue-50 p-3 text-center">
            <p className="text-sm text-blue-800">{message}</p>
          </div>
        )}

        {/* エラー表示 */}
        {error && (
          <div className="mt-4 w-[348px] rounded-md bg-red-50 p-3 text-center">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* メール・パスワードログインフォーム */}
        <form onSubmit={handleEmailLogin} className="mt-8 flex w-[348px] flex-col gap-4">
          <input
            type="email"
            placeholder="メールアドレス"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-[48px] w-full rounded-md border border-gray-300 px-4 text-[14px] text-black placeholder:text-gray-400 focus:border-[#52a2e8] focus:outline-none"
          />
          <input
            type="password"
            placeholder="パスワード"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-[48px] w-full rounded-md border border-gray-300 px-4 text-[14px] text-black placeholder:text-gray-400 focus:border-[#52a2e8] focus:outline-none"
          />
          <button
            type="submit"
            disabled={loading}
            className="h-[55px] w-full rounded-[25px] bg-[#52a2e8] text-[15px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {loading ? 'ログイン中...' : 'メールでログイン'}
          </button>
        </form>

        {/* 区切り線 */}
        <div className="mt-6 flex w-[348px] items-center">
          <div className="flex-1 border-t border-gray-300"></div>
          <span className="px-4 text-sm text-gray-500">または</span>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        {/* Googleログインボタン */}
        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          className="mt-6 flex h-[55px] w-[348px] items-center justify-center gap-3 rounded-[25px] border border-gray-300 bg-white text-[15px] font-semibold text-gray-700 transition-opacity hover:bg-gray-50 disabled:opacity-50"
        >
          {!loading && (
            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          )}
          {loading ? 'ログイン中...' : 'Googleでログイン'}
        </button>

        {/* 新規登録リンク */}
        <Link
          href={
            redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('//')
              ? `${PATHS.AUTH.SIGNUP.STEP1}?redirect=${encodeURIComponent(redirectPath)}`
              : PATHS.AUTH.SIGNUP.STEP1
          }
          className="mt-[23px] text-center text-[14px] font-semibold leading-[21px] text-[#52a2e8] underline decoration-solid [text-underline-position:from-font]"
        >
          はじめてライブショッピングをする方
        </Link>

        <p className="mt-5 px-4 text-center text-[12px] font-medium leading-6 text-black">
          ログインすることで
          <Link href={PATHS.TERMS} className="mx-1 text-[#52a2e8] underline">
            利用規約
          </Link>
          と
          <Link href={PATHS.PRIVACY} className="mx-1 text-[#52a2e8] underline">
            プライバシーポリシー
          </Link>
          に同意したものとみなします
        </p>

        {/* Nomissロゴ（下部） */}
        <div className="absolute bottom-[40px] flex items-center gap-2">
          <div className="relative h-[43px] w-[39px]">
            <Image src="/image/nomiss-icon.png" alt="Nomiss" fill className="object-contain" />
          </div>
          <span className="text-[20px] font-semibold leading-[21px] tracking-[-0.32px] text-[#1b95e1]">
            Nomiss
          </span>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
